import { z } from "zod";
import type { FunnelGroup, FunnelReport, FunnelTransition } from "./funnel";

/**
 * B1 wave 2: the funnel block of the weekly scoreboard (revenue build list B1, "a funnel block in the weekly
 * scoreboard, split by inquiry type and price band").
 *
 * The figures are `funnelReport`'s (funnel.ts), derived in code from rows that already exist; this file only
 * stores them and words them, so the model never touches a number (CLAUDE.md section 4). It follows the
 * scoreboard's own conventions: counts as `num of den`, a figure below its sample floor is `not measured` in
 * words and never a zero in its place, and no rounded point rate is printed (TA-251): the only percentages are the
 * two ends of the Wilson 95% interval, which are the interval and not a rate anyone reads a trend off.
 *
 * It lives in its own file, with the stage and terminal words restated here and pinned to funnel.ts by a test,
 * because funnel.ts reads the scoreboard's floors and scoreboard.ts stores this block: importing funnel.ts at run
 * time from here would be a cycle.
 *
 * THE COHORT (assumption, logged in the findings): the new inquiries of the 30 days up to the end of the reported
 * week, each followed to the moment the report ran. One week alone would almost never reach the 20-thread floor,
 * and 30 days is the window `pnpm funnel` and `pnpm measure:sales` already default to.
 */

export const SCOREBOARD_FUNNEL_COHORT_DAYS = 30;

export const SCOREBOARD_FUNNEL_STAGES = [
  "first_inbound",
  "first_reply",
  "contact_captured",
  "consult_proposed",
  "consult_booked",
  "consult_attended",
  "deposit",
  "sold",
] as const;
export const SCOREBOARD_FUNNEL_TERMINALS = ["no_show", "lost", "ghosted"] as const;

const count = z.number().int().nonnegative();
const stage = z.enum(SCOREBOARD_FUNNEL_STAGES);
const KEY = /^[A-Za-z0-9_.:-]{1,128}$/;

const stageCounts = z.strictObject({
  first_inbound: count,
  first_reply: count,
  contact_captured: count,
  consult_proposed: count,
  consult_booked: count,
  consult_attended: count,
  deposit: count,
  sold: count,
});

const notMeasured = z.literal("not_measured");

const transitionSchema = z.strictObject({
  from: stage,
  to: stage,
  denominator: count,
  numerator: count,
  rate: z.union([notMeasured, z.strictObject({ bp: count, lowBp: count, highBp: count })]),
  medianMs: z.union([notMeasured, count]),
  gapSamples: count,
});

const groupSchema = z.strictObject({
  key: z.string().regex(KEY),
  threads: count,
  stageCounts,
  terminals: z.strictObject({ no_show: count, lost: count, ghosted: count }),
  transitions: z.array(transitionSchema),
  negativeGaps: count,
});

/** The funnel block of `scoreboard.reported`: stage names, inquiry-type and band codes, and counts. No customer word. */
export const funnelBlockSchema = z.strictObject({
  minSample: count,
  cohortDays: count,
  /** The day the cohort ends (exclusive), the end of the reported week: `YYYY-MM-DD`. */
  untilDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  all: groupSchema,
  byInquiryType: z.array(groupSchema),
  byPriceBand: z.array(groupSchema),
});
export type FunnelBlock = z.infer<typeof funnelBlockSchema>;

/**
 * The report as the stored block. Parsed through the strict schema, so a report that drifted from the stored shape
 * throws here instead of being stored half-true.
 */
export function buildFunnelBlock(report: FunnelReport, o: { readonly untilDay: string }): FunnelBlock {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(o.untilDay)) throw new Error(`buildFunnelBlock: untilDay ${o.untilDay} is not a YYYY-MM-DD day.`);
  return funnelBlockSchema.parse({
    minSample: report.minSample,
    cohortDays: SCOREBOARD_FUNNEL_COHORT_DAYS,
    untilDay: o.untilDay,
    all: report.all,
    byInquiryType: report.byInquiryType,
    byPriceBand: report.byPriceBand,
  });
}

// ---- the digest lines ----

const pct = (bp: number): string => `${(bp / 100).toFixed(1)}%`;

/** A span in the unit a reader can hold: minutes under two hours, hours under two days, else days. */
function span(ms: number): string {
  const min = ms / 60_000;
  if (min < 120) return `${min.toFixed(1)} min`;
  const h = min / 60;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} days`;
}

function step(t: FunnelTransition, minSample: number): string {
  if (t.rate === "not_measured") return `${t.from} to ${t.to}: not measured, ${t.denominator} seen and ${minSample} needed`;
  const median =
    t.medianMs === "not_measured"
      ? `median not measured, ${t.gapSamples} seen and ${minSample} needed`
      : `median ${span(t.medianMs)} over ${t.gapSamples}`;
  return `${t.from} to ${t.to}: ${t.numerator} of ${t.denominator} (95% interval ${pct(t.rate.lowBp)} to ${pct(t.rate.highBp)}); ${median}`;
}

/**
 * The funnel as digest lines. The overall funnel in full; each split group as a heading with how many of its steps
 * reached the floor, and only the steps that did printed under it (a thin step of a split is counted, not listed
 * seven times over). The full figures, thin ones included, stay in the stored block.
 */
export function funnelDigestLines(f: FunnelBlock): string[] {
  const lines = [`Funnel, new inquiries of the ${f.cohortDays} days to ${f.untilDay}, followed to the report: ${f.all.threads} threads`];
  lines.push(`  reached: ${SCOREBOARD_FUNNEL_STAGES.map((s) => `${s} ${f.all.stageCounts[s]}`).join(", ")}`);
  lines.push(`  ended at: ${SCOREBOARD_FUNNEL_TERMINALS.map((t) => `${t} ${f.all.terminals[t]}`).join(", ")}`);
  for (const t of f.all.transitions) lines.push(`  ${step(t, f.minSample)}`);
  const split = (g: FunnelGroup | FunnelBlock["all"], heading: string): void => {
    const measured = g.transitions.filter((t) => t.rate !== "not_measured");
    lines.push(`  ${heading} ${g.key}: ${g.threads} threads, ${measured.length} of ${g.transitions.length} steps measured`);
    for (const t of measured) lines.push(`    ${step(t, f.minSample)}`);
  };
  for (const g of f.byInquiryType) split(g, "inquiry type");
  for (const g of f.byPriceBand) split(g, "price band");
  return lines;
}
